import type { ObjectGraph } from '@jet/environment/dependencies/object-graph';
import type { IntentController } from '@jet/environment/dispatching';
import {
    type PaginationIntent,
    PaginationIntentKind,
} from './PaginationIntent';
import { requestClassicalAPIResource } from '../../utilities/requestClassicalAPIResource';
import { ClassicalAPIURL } from '../../api/ClassicalAPIURL';
import type {
    ClassicalListItem,
    ClassicalPaginatedResponse,
} from '@jet-app/music-classical/api/types';
import { transformTracks } from '@jet-app/music-classical/utilities/transforms/tracks';
import { makeOnListItemClickAction } from '@jet-app/music-classical/action-model/onListItemClickAction';
import { fixArtwork } from '@jet-app/music-classical/models/artwork';
import { getListViewItem } from '@jet-app/music-classical/utilities/transforms/list-view';

export const PaginationIntentController: IntentController<PaginationIntent> = {
    $intentKind: PaginationIntentKind.Name,

    async perform(
        intent: PaginationIntent,
        objectGraph: ObjectGraph,
    ): Promise<ClassicalPaginatedResponse> {
        return getNextPage(intent.path, intent.limit, objectGraph);
    },
};

export async function getNextPage(
    path: string,
    limit: number,
    objectGraph: ObjectGraph,
): Promise<ClassicalPaginatedResponse> {
    const url = new ClassicalAPIURL(path, objectGraph);
    const paginationResponse = await requestClassicalAPIResource(
        { url: url.string },
        objectGraph,
    );

    if (paginationResponse?.statusCode === 200) {
        let response =
            paginationResponse?.body as unknown as ClassicalPaginatedResponse;

        if (response?.items?.[0]?.type === 'subheading') {
            // Library Track list items
            const tracksSection = transformTracks(
                { firstPage: { items: response.items } },
                objectGraph,
            );

            response = {
                ...response,
                items: tracksSection.items,
            };
        } else if (response.type === 'list') {
            // General list item
            response = {
                ...response,
                items: response.items.map((item: ClassicalListItem) => ({
                    ...getListViewItem(item),
                    artwork: fixArtwork(item.image),
                    clickAction: makeOnListItemClickAction(item.id, item.type),
                })),
            };
        }

        if (response.items.length < limit && response.next) {
            const { items, ...rest } = await getNextPage(
                response.next,
                limit,
                objectGraph,
            );

            return {
                ...rest,
                items: [...response.items, ...items],
            };
        }

        return response;
    }
}
